import { useState } from 'react';
import {
  AppError,
  contentCategories,
  type AdminContentArticle,
} from '@st/contract';
import {
  useAdminContent,
  useAdminContentMutations,
  useAdminProducts,
} from '../../api/queries';
import { QueryBoundary } from '../../components/QueryBoundary';
import { PageHeader } from '../../components/PageHeader';
import { useTranslation } from '../../i18n/I18nProvider';
import { toErrorMessage } from '../../i18n/errorMessage';
import { formatDate } from '../../lib/format';
import {
  Badge,
  Button,
  ConfirmDialog,
  DataTable,
  Input,
  Modal,
  Select,
  SkeletonList,
  Textarea,
  useToast,
  type Column,
} from '../../ui';

type ContentCategory = (typeof contentCategories)[number];

interface FormState {
  category: ContentCategory;
  slugHr: string;
  slugEn: string;
  titleHr: string;
  titleEn: string;
  bodyHr: string;
  bodyEn: string;
  requiresProductId: string;
}

const EMPTY_FORM: FormState = {
  category: 'training',
  slugHr: '',
  slugEn: '',
  titleHr: '',
  titleEn: '',
  bodyHr: '',
  bodyEn: '',
  requiresProductId: '',
};

function toForm(a: AdminContentArticle): FormState {
  return {
    category: a.category,
    slugHr: a.slugHr,
    slugEn: a.slugEn ?? '',
    titleHr: a.titleHr,
    titleEn: a.titleEn ?? '',
    bodyHr: a.bodyHr ?? '',
    bodyEn: a.bodyEn ?? '',
    requiresProductId: a.requiresProductId ?? '',
  };
}

/** Prazno polje znači „nema prijevoda", a to je null — ne prazan string. */
const orNull = (v: string): string | null => (v.trim() === '' ? null : v.trim());

/**
 * Edukacijski sadržaj (sekcija 40).
 *
 * Ovo je jedini način unosa u `content_articles` — bez njega je klijentska
 * sekcija Edukacija trajno prazna. Objava je odvojena od uređivanja: tekst se
 * sprema koliko god puta treba, a „Objavi" je zasebna, vidljiva odluka.
 *
 * Brisanje je nepovratno (server nema meko brisanje za članke), pa uvijek ide
 * kroz `ConfirmDialog` s imenom članka u pitanju.
 */
export function AdminContent() {
  const { t, locale } = useTranslation();
  const query = useAdminContent();
  const products = useAdminProducts();
  const { create, update, setPublished, remove } = useAdminContentMutations();
  const { toast } = useToast();

  const [editing, setEditing] = useState<AdminContentArticle | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [toDelete, setToDelete] = useState<AdminContentArticle | null>(null);

  const categoryLabel: Record<ContentCategory, string> = {
    nutrition: t('adminContent.catNutrition'),
    training: t('adminContent.catTraining'),
    recovery: t('adminContent.catRecovery'),
    mobility: t('adminContent.catMobility'),
    lifestyle: t('adminContent.catLifestyle'),
  };

  function patch(next: Partial<FormState>) {
    setForm((f) => ({ ...f, ...next }));
  }

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFieldErrors({});
    setFormOpen(true);
  }

  function openEdit(row: AdminContentArticle) {
    setEditing(row);
    setForm(toForm(row));
    setFieldErrors({});
    setFormOpen(true);
  }

  async function submit() {
    setFieldErrors({});
    const payload = {
      category: form.category,
      slugHr: form.slugHr.trim(),
      slugEn: orNull(form.slugEn),
      titleHr: form.titleHr.trim(),
      titleEn: orNull(form.titleEn),
      bodyHr: orNull(form.bodyHr),
      bodyEn: orNull(form.bodyEn),
      requiresProductId: form.requiresProductId === '' ? null : form.requiresProductId,
    };

    try {
      if (editing) {
        await update.mutateAsync({ id: editing.id, body: payload });
        toast(t('adminContent.saved'), 'success');
      } else {
        await create.mutateAsync(payload);
        toast(t('adminContent.created'), 'success');
      }
      setFormOpen(false);
      setForm(EMPTY_FORM);
      setEditing(null);
    } catch (err) {
      // 409 i 422 nose ime polja (zauzet slug, nepostojeći proizvod) — greška
      // ostaje uz polje koje ju je izazvalo, ne u toastu.
      if (err instanceof AppError && err.fields) {
        setFieldErrors(err.fields);
        return;
      }
      toast(toErrorMessage(t, err).title, 'danger');
    }
  }

  async function togglePublished(row: AdminContentArticle) {
    try {
      await setPublished.mutateAsync({ id: row.id, published: !row.published });
      toast(row.published ? t('adminContent.unpublished') : t('adminContent.published'), 'success');
    } catch (err) {
      toast(toErrorMessage(t, err).title, 'danger');
    }
  }

  async function confirmDelete() {
    if (!toDelete) return;
    try {
      await remove.mutateAsync(toDelete.id);
      toast(t('adminContent.deleted'), 'success');
    } catch (err) {
      toast(toErrorMessage(t, err).title, 'danger');
    } finally {
      setToDelete(null);
    }
  }

  const columns: readonly Column<AdminContentArticle>[] = [
    {
      key: 'title',
      header: t('adminContent.titleHr'),
      render: (r) => (
        <span className="flex flex-col">
          <span className="font-medium text-ink">{r.titleHr}</span>
          <span className="text-[12px] text-ink-muted">/{r.slugHr}</span>
        </span>
      ),
    },
    {
      key: 'titleEn',
      header: t('adminContent.titleEn'),
      hideOnMobile: true,
      render: (r) =>
        r.titleEn ?? <span className="text-ink-muted">{t('adminContent.noTranslation')}</span>,
    },
    {
      key: 'category',
      header: t('adminContent.category'),
      hideOnMobile: true,
      render: (r) => categoryLabel[r.category],
    },
    {
      key: 'access',
      header: t('adminContent.access'),
      hideOnMobile: true,
      render: (r) =>
        r.requiresProductId
          ? (products.data?.items.find((p) => p.id === r.requiresProductId)?.nameHr ??
            t('adminContent.lockedGeneric'))
          : t('adminContent.freeForAll'),
    },
    {
      key: 'updated',
      header: t('admin.createdAt'),
      hideOnMobile: true,
      render: (r) => formatDate(r.updatedAt, locale),
    },
    {
      key: 'status',
      header: t('adminContent.status'),
      align: 'right',
      render: (r) => (
        <span className="flex flex-wrap items-center justify-end gap-2">
          <Badge tone={r.published ? 'success' : 'neutral'}>
            {r.published ? t('adminContent.statusPublished') : t('adminContent.statusDraft')}
          </Badge>
          <Button variant="ghost" size="sm" onClick={() => openEdit(r)}>
            {t('action.edit')}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => void togglePublished(r)}>
            {r.published ? t('adminContent.unpublish') : t('adminContent.publish')}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setToDelete(r)}>
            {t('action.delete')}
          </Button>
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow={t('shell.admin')}
        title={t('adminContent.title')}
        subtitle={t('adminContent.subtitle')}
        action={
          <Button size="sm" onClick={openCreate}>
            {t('adminContent.create')}
          </Button>
        }
      />

      <QueryBoundary
        query={query}
        skeleton={<SkeletonList rows={4} />}
        isEmpty={(d) => d.items.length === 0}
        empty={{
          message: t('adminContent.empty'),
          actionLabel: t('adminContent.create'),
          onAction: openCreate,
        }}
      >
        {(data) => (
          <DataTable
            caption={t('adminContent.title')}
            rows={data.items}
            rowKey={(r) => r.id}
            columns={columns}
          />
        )}
      </QueryBoundary>

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editing ? t('adminContent.edit') : t('adminContent.create')}
        closeLabel={t('common.close')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setFormOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button loading={create.isPending || update.isPending} onClick={() => void submit()}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <Select
            id="ac-category"
            label={t('adminContent.category')}
            value={form.category}
            onChange={(e) => patch({ category: e.target.value as ContentCategory })}
            options={contentCategories.map((c) => ({ value: c, label: categoryLabel[c] }))}
            error={fieldErrors.category}
          />
          <Input
            id="ac-title-hr"
            label={t('adminContent.titleHr')}
            value={form.titleHr}
            onChange={(e) => patch({ titleHr: e.target.value })}
            error={fieldErrors.titleHr}
            required
          />
          <Input
            id="ac-slug-hr"
            label={t('adminContent.slugHr')}
            hint={t('adminContent.slugHint')}
            value={form.slugHr}
            autoComplete="off"
            onChange={(e) => patch({ slugHr: e.target.value })}
            error={fieldErrors.slugHr}
            required
          />
          <Textarea
            id="ac-body-hr"
            label={t('adminContent.bodyHr')}
            hint={t('adminContent.bodyHint')}
            rows={8}
            value={form.bodyHr}
            onChange={(e) => patch({ bodyHr: e.target.value })}
            error={fieldErrors.bodyHr}
          />
          <Input
            id="ac-title-en"
            label={t('adminContent.titleEn')}
            hint={t('action.optional')}
            value={form.titleEn}
            onChange={(e) => patch({ titleEn: e.target.value })}
            error={fieldErrors.titleEn}
          />
          <Input
            id="ac-slug-en"
            label={t('adminContent.slugEn')}
            hint={t('adminContent.slugEnHint')}
            value={form.slugEn}
            autoComplete="off"
            onChange={(e) => patch({ slugEn: e.target.value })}
            error={fieldErrors.slugEn}
          />
          <Textarea
            id="ac-body-en"
            label={t('adminContent.bodyEn')}
            hint={t('action.optional')}
            rows={8}
            value={form.bodyEn}
            onChange={(e) => patch({ bodyEn: e.target.value })}
            error={fieldErrors.bodyEn}
          />
          <Select
            id="ac-product"
            label={t('adminContent.requiresProduct')}
            hint={t('adminContent.requiresProductHint')}
            value={form.requiresProductId}
            onChange={(e) => patch({ requiresProductId: e.target.value })}
            options={[
              { value: '', label: t('adminContent.freeForAll') },
              ...(products.data?.items ?? []).map((p) => ({ value: p.id, label: p.nameHr })),
            ]}
            error={fieldErrors.requiresProductId}
          />
        </div>
      </Modal>

      <ConfirmDialog
        open={toDelete !== null}
        title={t('adminContent.deleteTitle')}
        question={t('adminContent.deleteQuestion', { title: toDelete?.titleHr ?? '' })}
        confirmLabel={t('action.confirmDelete')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        loading={remove.isPending}
        onConfirm={() => void confirmDelete()}
        onClose={() => setToDelete(null)}
      />
    </>
  );
}
